import { type ReactNode } from "react";
import { BackArrowIcon, Help_1Icon } from "vega-icons";

import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { Footer } from "@vega-core/components/Layout/Footer/Footer";
import { GroupedMenu } from "@vega-core/components/Navigation/GroupedMenu/GroupedMenu";
import { type MenuGroupConfig } from "@vega-core/components/Navigation/GroupedMenu/types";
import { m } from "@vega-core/paraglide/messages";
import { getIsLPCoInvestPortal } from "@vega-core/utils/global";

import { CommonPaths } from "../../../paths";

type Props = {
  menuConfig: MenuGroupConfig[];
  orgSelector: ReactNode;
  onHelpClick: VoidFunction;
  showMenu: boolean;
  onMenuToggle: (show: boolean) => void;
  children: ReactNode;
};

export function MobileShell({
  menuConfig,
  orgSelector,
  onHelpClick,
  showMenu,
  onMenuToggle,
  children,
}: Props) {
  const isLPCoInvestPortal = getIsLPCoInvestPortal();

  const helpButton = !isLPCoInvestPortal ? (
    <ThemedButton
      dataTestId="need-help-button"
      icon={<Help_1Icon />}
      onClick={onHelpClick}
      variant="secondary"
    />
  ) : null;

  if (showMenu) {
    return (
      <div className="flex flex-col h-dvh bg-background/primary">
        <div className="flex items-center justify-between p-4 border-b border-border/primary shrink-0">
          <ThemedButton
            className="px-0"
            dataTestId="settings-go-back-home"
            route={CommonPaths.Home}
            variant="tertiary"
          >
            <div className="flex items-center gap-2">
              <BackArrowIcon />
              {m.btn_back()}
            </div>
          </ThemedButton>
          {helpButton}
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          <p className="heading-4 mb-2">{m.titles_settings()}</p>
          {orgSelector}
          <div className="[&_[data-testid=menu-group-container]]:border-0">
            <GroupedMenu config={menuConfig} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-dvh bg-background/primary">
      <div className="flex items-center justify-between p-4 border-b border-border/primary shrink-0">
        <ThemedButton
          className="px-0"
          dataTestId="settings-back-to-menu"
          onClick={() => onMenuToggle(true)}
          variant="tertiary"
        >
          <div className="flex items-center gap-2">
            <BackArrowIcon />
            {m.titles_settings()}
          </div>
        </ThemedButton>
        {helpButton}
      </div>
      <div className="flex flex-1 flex-col overflow-y-auto p-4">
        <div className="w-full flex-1">{children}</div>
        <Footer isLoggedIn />
      </div>
    </div>
  );
}
